import { useEffect, useMemo, useRef } from "react";
import kountSDK from "@kount/kount-web-client-sdk";

import { env } from "@/env";

function generateSessionId(): string {
  return crypto.randomUUID().replace(/-/g, "").slice(0, 32);
}

// TODO(C1-followup): useKount unit tests (undefined no-op, single SDK call, env-change rerun,
// clientId resolution) require jsdom + @testing-library/react infrastructure. fluid-checkout's
// current test setup (Playwright e2e only, no Jest/Vitest, no @testing-library/react) doesn't
// support hook rendering tests. Defer to a follow-up that adds the test infra.
// CURRENT-989 documents the deferral.
export function useKount(
  environment: "TEST" | "PROD" | undefined,
  clientId?: string | null,
) {
  // Compute the session id during render — pure, no side effects.
  // Stable per environment so a strict-mode double-render doesn't generate
  // two ids and the value is available to consumers immediately.
  const sessionId = useMemo(() => {
    if (typeof window === "undefined") return undefined;
    if (!environment) return undefined;
    return generateSessionId();
  }, [environment]);

  // Initialize the SDK in an effect — side effects belong here, not in
  // useMemo. The ref guards against re-initializing on strict-mode double-fire
  // (dev) when sessionId AND the resolved client id are both unchanged. The
  // composite guard key ensures that a cart-driven flip of clientId (e.g.
  // portfolio → enterprise once the cart hydrates) re-runs the SDK with the
  // correct merchant tenant.
  const initializedRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!environment || !sessionId) return;

    const resolvedClientId = clientId || env.NEXT_PUBLIC_KOUNT_CLIENT_ID;
    if (!resolvedClientId) return;

    const guardKey = `${sessionId}:${resolvedClientId}`;
    if (initializedRef.current === guardKey) return;
    initializedRef.current = guardKey;

    kountSDK(
      {
        clientID: resolvedClientId,
        environment,
        isSinglePageApp: false,
      },
      sessionId,
    );
  }, [environment, sessionId, clientId]);

  return sessionId;
}
